import { FC, useLayoutEffect, useEffect, useMemo, useCallback } from 'react';
import { useMutation } from 'react-query';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { AppLoader } from 'components/Core/AppLoader';
import BrandsTable from 'components/Tables/Brand/BrandsTable/BrandsTable';
import { Button } from 'components/Core/Button';
import { CreatedBySelect } from 'components/Inputs/CreatedBySelect';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import { brandsTranslations } from './translations';
import { BrandsTransFields, IBrandsFilters } from './types';
import { availableRoles } from './constants';
import styles from './brands.module.scss';
import IsDeletedSelect from 'components/Inputs/IsDeletedSelect';

const Brands: FC = observer(() => {
  const {
    brandsStore: { getBrandsData, brandsQuery, clearData },
    mainStore: { user },
  } = useStore();

  const { t } = useMyTranslations<BrandsTransFields>([brandsTranslations.name]);

  useLayoutEffect(() => {
    clearData();
  }, []);

  const navigate = useNavigate();

  const { isLoading, mutate } = useMutation(getBrandsData, {
    mutationKey: 'getBrandsDataWithParams',
  });

  useEffect(() => {
    mutate(brandsQuery);
  }, [user?.selectedCountryCode]);

  const useFormMethods = useForm<IBrandsFilters>({
    defaultValues: {
      ...brandsQuery,
    },
  });

  const { handleSubmit } = useFormMethods;

  const onSubmit = useCallback((data: IBrandsFilters) => {
    mutate(data);
  }, []);

  const showBtn = useMemo(() => user && availableRoles.includes(user.role), [user?.role]);

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <h2 className={styles.headerTitle}>{t('brandsList')}</h2>
      </header>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <FormProvider {...(useFormMethods as any)}>
        <div className={styles.filters}>
          <IsDeletedSelect />
          <CreatedBySelect />
          <ControlledInput
            name='search'
            type='text'
            placeholder={t('search')}
            isRemoveMargin
            classNameProp={styles.searchInput}
          />
          <Button
            title={t('search')}
            onClick={handleSubmit(onSubmit)}
            classNameProp={`${styles.btn} ${styles.searchBtn}`}
            isDisabled={isLoading}
          />
          {showBtn && <Button title={t('addBtn')} onClick={() => navigate(`add`)} classNameProp={styles.btn} />}
        </div>
      </FormProvider>
      {isLoading ? <AppLoader /> : <BrandsTable />}
    </main>
  );
});

export default Brands;
